import { getSupabaseClient } from "./index";
import {
  mapAuthDriverProfile,
  mapAuthRestaurant,
  mapAuthRestaurantList,
  mapDriverLedger,
  mapDriverRow,
  mapDriverTrip,
  mapPasscodeRow,
  validateDriverForm,
  type CreateDriverInput,
  type DriverAuthRestaurant,
  type DriverDetailPayload,
  type DriverFinanceSummary,
  type DriverLinkStatus,
  type DriverPasscodeRecord,
  type DriverRecord,
  type DriverStats,
  type DriverWalletInfo,
  type GeneratePasscodeInput,
  type LinkDriverRestaurantResult,
  type LoginDriverPasscodeResult,
  type PeekPasscodeResult,
  type RedeemDriverPasscodeInput,
  type RedeemDriverPasscodeResult,
  type UpdateDriverInput,
} from "@veorest/utils";

export type {
  CreateDriverInput,
  DriverAuthRestaurant,
  DriverDetailPayload,
  DriverFinanceSummary,
  DriverPasscodeRecord,
  DriverRecord,
  DriverStats,
  DriverWalletInfo,
  GeneratePasscodeInput,
  LinkDriverRestaurantResult,
  LoginDriverPasscodeResult,
  PeekPasscodeResult,
  RedeemDriverPasscodeInput,
  RedeemDriverPasscodeResult,
  UpdateDriverInput,
};

function rpcErrorMessage(error: { message?: string; code?: string } | null): string {
  const msg = String(error?.message ?? "unknown_error");
  if (error?.code === "PGRST202" || /could not find the function/i.test(msg)) {
    return "rpc_missing";
  }
  return msg;
}

export async function listDrivers(tenantId: string): Promise<DriverRecord[]> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("list_tenant_drivers", {
    p_tenant_id: tenantId,
  });
  if (error) throw error;
  return (data ?? []).map((row: Record<string, unknown>) => mapDriverRow(row));
}

export async function getDriverDetail(
  tenantId: string,
  driverId: string
): Promise<DriverDetailPayload> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("get_driver_detail", {
    p_tenant_id: tenantId,
    p_driver_id: driverId,
  });
  if (error) throw new Error(rpcErrorMessage(error));

  const payload = (data ?? {}) as Record<string, unknown>;
  const driverRaw = (payload.driver ?? {}) as Record<string, unknown>;
  const walletRaw = (payload.wallet ?? {}) as Record<string, unknown>;
  const financeRaw = (payload.finance ?? {}) as Record<string, unknown>;
  const tripsRaw = (payload.trips ?? []) as Record<string, unknown>[];
  const ledgerRaw = (payload.ledger ?? []) as Record<string, unknown>[];

  const wallet: DriverWalletInfo = {
    id: (walletRaw.id as string) || null,
    balance: Number(walletRaw.balance ?? 0),
    currency: String(walletRaw.currency ?? "USD"),
    updated_at: String(walletRaw.updated_at ?? new Date().toISOString()),
  };

  const finance: DriverFinanceSummary = {
    cod_pending: Number(financeRaw.cod_pending ?? 0),
    cod_collected: Number(financeRaw.cod_collected ?? 0),
    trip_fees_total: Number(financeRaw.trip_fees_total ?? 0),
    active_trips: Number(financeRaw.active_trips ?? 0),
    wallet_balance: Number(financeRaw.wallet_balance ?? wallet.balance),
  };

  return {
    driver: mapDriverRow({
      ...driverRaw,
      wallet_balance: finance.wallet_balance,
      trips_count: tripsRaw.length,
      cod_pending_total: finance.cod_pending,
      trip_fees_total: finance.trip_fees_total,
      assigned_orders_count: finance.active_trips,
    }),
    wallet,
    finance,
    trips: tripsRaw.map(mapDriverTrip),
    ledger: ledgerRaw.map(mapDriverLedger),
  };
}

export async function getDriverStats(tenantId: string): Promise<DriverStats> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("get_driver_module_stats", {
    p_tenant_id: tenantId,
  });
  if (error) throw error;
  const row = (data ?? {}) as Record<string, unknown>;
  return {
    total: Number(row.total ?? 0),
    active: Number(row.active ?? 0),
    online: Number(row.online ?? 0),
    on_delivery: Number(row.on_delivery ?? 0),
  };
}

export async function createDriver(
  tenantId: string,
  input: CreateDriverInput
): Promise<{ driver: DriverRecord; reused: boolean }> {
  const validation = validateDriverForm({
    name: input.name,
    phone: input.phone,
    email: input.email ?? undefined,
  });
  if (validation) throw new Error(validation);

  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("create_tenant_driver", {
    p_tenant_id: tenantId,
    p_name: input.name.trim(),
    p_phone: input.phone.trim(),
    p_email: input.email?.trim() || null,
    p_vehicle_type: input.vehicle_type ?? "motorcycle",
    p_vehicle_number: input.vehicle_number?.trim() || null,
    p_vehicle_model: input.vehicle_model?.trim() || null,
    p_vehicle_year: input.vehicle_year ?? null,
    p_vehicle_color: input.vehicle_color?.trim() || null,
    p_photo_url: input.photo_url?.trim() || null,
    p_collects_cod: input.collects_cod ?? true,
    p_reuse_existing: input.reuse_existing ?? true,
  });

  if (error) throw new Error(rpcErrorMessage(error));

  const payload = data as {
    driver: Record<string, unknown>;
    link: Record<string, unknown>;
    reused: boolean;
  };

  return {
    driver: mapDriverRow({
      ...payload.driver,
      link_status: payload.link?.status ?? "active",
      link_created_at: payload.link?.created_at,
      assigned_orders_count: 0,
      wallet_balance: 0,
      trips_count: 0,
      cod_pending_total: 0,
      trip_fees_total: 0,
    }),
    reused: Boolean(payload.reused),
  };
}

export async function updateDriver(
  tenantId: string,
  driverId: string,
  patch: UpdateDriverInput
): Promise<DriverRecord> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("update_tenant_driver", {
    p_tenant_id: tenantId,
    p_driver_id: driverId,
    p_patch: patch,
  });
  if (error) throw new Error(rpcErrorMessage(error));

  return mapDriverRow({
    ...(data as Record<string, unknown>),
    link_status: "active",
    assigned_orders_count: 0,
    wallet_balance: 0,
    trips_count: 0,
    cod_pending_total: 0,
    trip_fees_total: 0,
  });
}

export async function setDriverLinkStatus(
  tenantId: string,
  driverId: string,
  status: "active" | "blocked"
): Promise<void> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.rpc("set_driver_restaurant_status", {
    p_tenant_id: tenantId,
    p_driver_id: driverId,
    p_status: status,
  });
  if (error) throw new Error(rpcErrorMessage(error));
}

export async function removeDriverFromRestaurant(
  tenantId: string,
  driverId: string
): Promise<void> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.rpc("remove_driver_from_restaurant", {
    p_tenant_id: tenantId,
    p_driver_id: driverId,
  });
  if (error) throw new Error(rpcErrorMessage(error));
}

function makeInviteCode(): string {
  const bytes = new Uint8Array(4);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, 6)
    .toUpperCase();
}

/**
 * Passcodes use direct table access (not RPC).
 * PostgREST on this project keeps returning 404 for new RPCs even after CREATE FUNCTION.
 */
export async function listDriverPasscodes(
  tenantId: string
): Promise<DriverPasscodeRecord[]> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("driver_passcodes")
    .select("*")
    .eq("tenant_id", tenantId)
    .order("created_at", { ascending: false })
    .limit(50);

  if (error) throw error;
  return (data ?? []).map((row: Record<string, unknown>) => mapPasscodeRow(row));
}

export async function generateDriverPasscode(
  tenantId: string,
  input: GeneratePasscodeInput
): Promise<DriverPasscodeRecord> {
  const name = input.ref_driver_name?.trim();
  if (!name) throw new Error("ref_driver_name_required");

  const phone = input.ref_driver_phone?.trim() || null;
  const expiresHours = input.expires_hours && input.expires_hours > 0 ? input.expires_hours : 72;
  const expiresAt = new Date(Date.now() + expiresHours * 60 * 60 * 1000).toISOString();
  const supabase = getSupabaseClient();

  let lastError: Error | null = null;

  for (let attempt = 0; attempt < 6; attempt++) {
    const code = makeInviteCode();
    const fullRow = {
      tenant_id: tenantId,
      code,
      label: name,
      ref_driver_name: name,
      ref_driver_phone: phone,
      is_reusable: false,
      max_uses: 1,
      use_count: 0,
      expires_at: expiresAt,
      is_active: true,
    };

    let { data, error } = await supabase
      .from("driver_passcodes")
      .insert(fullRow)
      .select("*")
      .single();

    // Columns may not be in API cache yet — retry with label-only payload
    if (error && /ref_driver|schema cache|Could not find/i.test(error.message ?? "")) {
      const legacyLabel = phone ? `${name} · ${phone}` : name;
      ({ data, error } = await supabase
        .from("driver_passcodes")
        .insert({
          tenant_id: tenantId,
          code,
          label: legacyLabel,
          is_reusable: false,
          max_uses: 1,
          use_count: 0,
          expires_at: expiresAt,
          is_active: true,
        })
        .select("*")
        .single());
    }

    if (!error && data) {
      const row = mapPasscodeRow(data as Record<string, unknown>);
      return {
        ...row,
        ref_driver_name: row.ref_driver_name || name,
        ref_driver_phone: row.ref_driver_phone || phone,
      };
    }

    // Unique code collision — try another code
    if (error && /duplicate|unique/i.test(error.message ?? "")) {
      lastError = new Error(error.message);
      continue;
    }

    throw new Error(error?.message ?? "passcode_generate_failed");
  }

  throw lastError ?? new Error("passcode_generate_failed");
}

export async function revokeDriverPasscode(
  tenantId: string,
  passcodeId: string
): Promise<void> {
  const supabase = getSupabaseClient();
  const { error } = await supabase
    .from("driver_passcodes")
    .update({ is_active: false })
    .eq("tenant_id", tenantId)
    .eq("id", passcodeId);

  if (error) throw new Error(error.message);
}

function mapAuthSessionResult(row: Record<string, unknown>): RedeemDriverPasscodeResult {
  const driver = (row.driver ?? {}) as Record<string, unknown>;
  const link = (row.link ?? {}) as Record<string, unknown>;
  const session = row.session as Record<string, unknown> | null;
  const restaurants = mapAuthRestaurantList(row.restaurants);
  const restaurant = mapAuthRestaurant(row.restaurant as Record<string, unknown>);

  return {
    driver: mapAuthDriverProfile(driver),
    link: {
      id: String(link.id ?? ""),
      driver_id: String(link.driver_id ?? driver.id ?? ""),
      tenant_id: String(link.tenant_id ?? ""),
      status: ((link.status as DriverLinkStatus) || "active") as DriverLinkStatus,
    },
    restaurant,
    session: session
      ? {
          id: String(session.id ?? ""),
          driver_id: String(session.driver_id ?? ""),
          device_id: String(session.device_id ?? ""),
          last_active: String(session.last_active ?? ""),
        }
      : null,
    passcode: String(row.passcode ?? ""),
    reused: Boolean(row.reused),
    restaurants: restaurants.length > 0 ? restaurants : restaurant.id ? [restaurant] : [],
  };
}

/** Validate invite / login code without consuming it (anon-safe RPC). */
export async function peekDriverPasscode(code: string): Promise<PeekPasscodeResult> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("peek_driver_passcode", {
    p_code: code.trim().toUpperCase(),
  });
  if (error) throw new Error(rpcErrorMessage(error));
  const row = (data ?? {}) as Record<string, unknown>;
  if (!row.valid) throw new Error("passcode_invalid");

  const status = row.status === "claimed" ? "claimed" : "available";
  return {
    valid: true,
    status,
    passcode: String(row.passcode ?? code).toUpperCase(),
    restaurant: mapAuthRestaurant(row.restaurant as Record<string, unknown>),
    ref_driver_name: (row.ref_driver_name as string) || null,
    ref_driver_phone: (row.ref_driver_phone as string) || null,
    driver: status === "claimed" ? mapAuthDriverProfile(row.driver as Record<string, unknown>) : null,
    link_status: (row.link_status as DriverLinkStatus) || null,
  };
}

/** First claim: create a NEW driver, bind passcode permanently, open session. */
export async function redeemDriverPasscode(
  input: RedeemDriverPasscodeInput
): Promise<RedeemDriverPasscodeResult> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("redeem_driver_passcode", {
    p_code: input.code.trim().toUpperCase(),
    p_phone: input.phone.trim(),
    p_name: input.name.trim(),
    p_reuse_existing: false,
    p_vehicle_type: input.vehicle_type ?? "motorcycle",
    p_vehicle_number: input.vehicle_number?.trim() || null,
    p_vehicle_model: input.vehicle_model?.trim() || null,
    p_vehicle_year: input.vehicle_year ?? null,
    p_vehicle_color: input.vehicle_color?.trim() || null,
    p_email: input.email?.trim() || null,
    p_device_id: input.device_id?.trim() || null,
    p_photo_url: input.photo_url?.trim() || null,
  });
  if (error) throw new Error(rpcErrorMessage(error));
  return mapAuthSessionResult((data ?? {}) as Record<string, unknown>);
}

/** Relogin with a passcode already claimed by this driver. */
export async function loginDriverPasscode(
  code: string,
  deviceId?: string | null
): Promise<LoginDriverPasscodeResult> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("login_driver_passcode", {
    p_code: code.trim().toUpperCase(),
    p_device_id: deviceId?.trim() || null,
  });
  if (error) throw new Error(rpcErrorMessage(error));
  return mapAuthSessionResult((data ?? {}) as Record<string, unknown>);
}

/** List restaurants linked to a driver (Places tab). */
export async function listDriverRestaurants(
  driverId: string
): Promise<DriverAuthRestaurant[]> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("list_driver_restaurants", {
    p_driver_id: driverId,
  });
  if (error) throw new Error(rpcErrorMessage(error));
  const row = (data ?? {}) as Record<string, unknown>;
  return mapAuthRestaurantList(row.restaurants);
}

/**
 * Logged-in driver joins another restaurant with an unused invite code.
 * Reuses existing profile (photo, vehicle, etc.) — no re-onboard.
 */
export async function linkDriverRestaurantPasscode(
  code: string,
  driverId: string,
  deviceId?: string | null
): Promise<LinkDriverRestaurantResult> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("link_driver_restaurant_passcode", {
    p_code: code.trim().toUpperCase(),
    p_driver_id: driverId,
    p_device_id: deviceId?.trim() || null,
  });
  if (error) throw new Error(rpcErrorMessage(error));
  const row = (data ?? {}) as Record<string, unknown>;
  const link = (row.link ?? {}) as Record<string, unknown>;
  const restaurant = mapAuthRestaurant(row.restaurant as Record<string, unknown>);
  const restaurants = mapAuthRestaurantList(row.restaurants);

  return {
    driver: mapAuthDriverProfile(row.driver as Record<string, unknown>),
    link: {
      id: String(link.id ?? ""),
      driver_id: String(link.driver_id ?? driverId),
      tenant_id: String(link.tenant_id ?? restaurant.id),
      status: ((link.status as DriverLinkStatus) || "active") as DriverLinkStatus,
    },
    restaurant,
    passcode: String(row.passcode ?? code).toUpperCase(),
    restaurants: restaurants.length > 0 ? restaurants : restaurant.id ? [restaurant] : [],
  };
}

/** Keep driver online while the app is open (call every ~45s). */
export async function touchDriverPresence(
  driverId: string,
  deviceId: string
): Promise<void> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.rpc("touch_driver_presence", {
    p_driver_id: driverId,
    p_device_id: deviceId,
  });
  if (error) throw new Error(rpcErrorMessage(error));
}

/** Mark driver offline on sign-out. */
export async function logoutDriverSession(
  driverId: string,
  deviceId?: string | null
): Promise<void> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.rpc("logout_driver_session", {
    p_driver_id: driverId,
    p_device_id: deviceId?.trim() || null,
  });
  if (error) throw new Error(rpcErrorMessage(error));
}

/** Upload onboard selfie/avatar to public driver-photos bucket. */
export async function uploadDriverPhoto(file: File, deviceId: string): Promise<string> {
  const supabase = getSupabaseClient();
  const ext = (file.name.split(".").pop() || "jpg").toLowerCase().replace(/[^a-z0-9]/g, "");
  const path = `${deviceId}/${Date.now()}.${ext || "jpg"}`;
  const { error } = await supabase.storage.from("driver-photos").upload(path, file, {
    cacheControl: "3600",
    upsert: true,
    contentType: file.type || "image/jpeg",
  });
  if (error) throw new Error(error.message);
  const { data } = supabase.storage.from("driver-photos").getPublicUrl(path);
  return data.publicUrl;
}

export async function assignDriverToOrder(
  tenantId: string,
  orderId: string,
  driverId: string
): Promise<Record<string, unknown>> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("staff_assign_driver", {
    p_tenant_id: tenantId,
    p_order_id: orderId,
    p_driver_id: driverId,
  });
  if (error) throw new Error(rpcErrorMessage(error));
  const payload = data as { order: Record<string, unknown> };
  return payload.order;
}

export async function unassignDriverFromOrder(
  tenantId: string,
  orderId: string
): Promise<Record<string, unknown>> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("staff_unassign_driver", {
    p_tenant_id: tenantId,
    p_order_id: orderId,
  });
  if (error) throw new Error(rpcErrorMessage(error));
  const payload = data as { order: Record<string, unknown> };
  return payload.order;
}

/** Request an OELOX network driver (simple auto-assign or queue). */
export async function requestOeloxDriver(
  tenantId: string,
  orderId: string
): Promise<{
  ok: boolean;
  queued: boolean;
  assigned: boolean;
  driver_id?: string;
  message?: string;
}> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("staff_request_oelox_driver", {
    p_tenant_id: tenantId,
    p_order_id: orderId,
  });
  if (error) throw new Error(rpcErrorMessage(error));
  return data as {
    ok: boolean;
    queued: boolean;
    assigned: boolean;
    driver_id?: string;
    message?: string;
  };
}
